import { ExternalLink, Globe2, LocateFixed, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { RelTime } from "@/components/data/RelTime";
import { cn } from "@/lib/utils";
import type { AdminUser, LocationSnapshot } from "@/types";

export function locationFromUser(user: AdminUser): LocationSnapshot | null {
  const hasAny =
    user.last_location_latitude != null ||
    user.last_location_longitude != null ||
    !!user.last_location_country ||
    !!user.last_location_timezone ||
    !!user.last_location_city ||
    !!user.last_location_region ||
    !!user.last_location_at;

  if (!hasAny) return null;

  return {
    latitude: user.last_location_latitude ?? null,
    longitude: user.last_location_longitude ?? null,
    accuracy_meters: user.last_location_accuracy_meters ?? null,
    location_country: user.last_location_country ?? null,
    country: user.last_location_country ?? null,
    timezone: user.last_location_timezone ?? null,
    city: user.last_location_city ?? null,
    region: user.last_location_region ?? null,
    observed_at: user.last_location_at ?? null,
    source: user.last_location_latitude != null && user.last_location_longitude != null ? "device" : "ip",
  };
}

export function hasCoordinates(location: LocationSnapshot | null | undefined): location is LocationSnapshot & {
  latitude: number;
  longitude: number;
} {
  return Number.isFinite(location?.latitude) && Number.isFinite(location?.longitude);
}

export function locationCountry(location: LocationSnapshot | null | undefined): string | null {
  return location?.location_country ?? location?.country ?? null;
}

export function formatLocationLabel(location: LocationSnapshot | null | undefined, fallback = "No location"): string {
  if (!location) return fallback;
  const country = locationCountry(location);
  const locality = [location.city, location.region].filter(Boolean).join(", ");
  if (locality && country) return `${locality}, ${country}`;
  if (locality) return locality;
  if (country) return country;
  if (location.timezone) return location.timezone;
  if (hasCoordinates(location)) return formatCoordinates(location);
  return fallback;
}

export function formatCoordinates(location: LocationSnapshot & { latitude: number; longitude: number }): string {
  return `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`;
}

function formatAccuracy(meters: number | null | undefined): string | null {
  if (meters == null || !Number.isFinite(meters)) return null;
  if (meters >= 1000) return `${(meters / 1000).toFixed(meters >= 10_000 ? 0 : 1)} km`;
  return `${Math.round(meters)} m`;
}

function mapEmbedUrl(location: LocationSnapshot & { latitude: number; longitude: number }): string {
  const accuracy = Math.max(250, Math.min(location.accuracy_meters ?? 1200, 25_000));
  const span = Math.max(0.01, Math.min((accuracy / 111_000) * 4, 0.25));
  const west = location.longitude - span;
  const east = location.longitude + span;
  const south = location.latitude - span;
  const north = location.latitude + span;
  const bbox = [west, south, east, north].map((v) => v.toFixed(5)).join(",");
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${location.latitude.toFixed(5)},${location.longitude.toFixed(5)}`;
}

export function mapLinks(location: LocationSnapshot | null | undefined) {
  if (!hasCoordinates(location)) return null;
  const q = `${location.latitude.toFixed(5)},${location.longitude.toFixed(5)}`;
  return {
    osm: `https://www.openstreetmap.org/?mlat=${location.latitude.toFixed(5)}&mlon=${location.longitude.toFixed(5)}#map=15/${location.latitude.toFixed(5)}/${location.longitude.toFixed(5)}`,
    google: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`,
  };
}

export function LocationBadge({
  location,
  className,
  showTime = false,
}: {
  location: LocationSnapshot | null | undefined;
  className?: string;
  showTime?: boolean;
}) {
  if (!location) {
    return <span className="text-xs text-muted-foreground">-</span>;
  }

  const precise = hasCoordinates(location);
  const label = formatLocationLabel(location);
  const time = location.observed_at ?? location.created_at ?? null;

  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5", className)}>
      <Badge variant={precise ? "info" : "outline"} className="max-w-full gap-1">
        {precise ? <LocateFixed className="h-3 w-3 shrink-0" /> : <Globe2 className="h-3 w-3 shrink-0" />}
        <span className="truncate">{label}</span>
      </Badge>
      {showTime && time ? (
        <span className="shrink-0 text-[11px] text-muted-foreground">
          <RelTime value={time} />
        </span>
      ) : null}
    </span>
  );
}

export function LocationFacts({ location }: { location: LocationSnapshot | null | undefined }) {
  if (!location) {
    return <div className="text-xs text-muted-foreground">No location has been recorded for this user yet.</div>;
  }

  const precise = hasCoordinates(location);
  const accuracy = formatAccuracy(location.accuracy_meters);
  const links = mapLinks(location);

  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
      <Fact label="Place" value={formatLocationLabel(location)} />
      <Fact label="Source" value={precise ? "Device GPS" : "IP/country"} />
      {precise ? <Fact label="Coordinates" value={formatCoordinates(location)} mono /> : null}
      {accuracy ? <Fact label="Accuracy" value={accuracy} /> : null}
      {location.timezone ? <Fact label="Timezone" value={location.timezone} mono /> : null}
      {location.ip ? <Fact label="IP" value={location.ip} mono /> : null}
      {(location.observed_at ?? location.created_at) ? (
        <div>
          <dt className="text-muted-foreground">Seen</dt>
          <dd className="mt-0.5 text-foreground">
            <RelTime value={(location.observed_at ?? location.created_at)!} />
          </dd>
        </div>
      ) : null}
      {links ? (
        <div className="col-span-2 flex flex-wrap gap-2 pt-1">
          <a href={links.osm} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-300 hover:underline">
            OpenStreetMap <ExternalLink className="h-3 w-3" />
          </a>
          <a href={links.google} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sky-300 hover:underline">
            Google Maps <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      ) : null}
    </dl>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn("mt-0.5 truncate text-foreground", mono && "font-mono")}>{value}</dd>
    </div>
  );
}

export function LocationMap({
  location,
  className,
  heightClassName = "h-[260px]",
}: {
  location: LocationSnapshot | null | undefined;
  className?: string;
  heightClassName?: string;
}) {
  if (!hasCoordinates(location)) {
    return (
      <div
        className={cn(
          "grid place-items-center rounded-md border border-border/60 bg-secondary/30 p-5 text-center",
          heightClassName,
          className
        )}
      >
        <div>
          <div className="mx-auto grid h-9 w-9 place-items-center rounded-md bg-secondary text-muted-foreground">
            <MapPin className="h-4 w-4" />
          </div>
          <div className="mt-3 text-sm font-medium">{formatLocationLabel(location, "No precise map point")}</div>
          <div className="mt-1 text-xs text-muted-foreground">Waiting for a request with device coordinates.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("overflow-hidden rounded-md border border-border/60 bg-secondary/30", heightClassName, className)}>
      <iframe
        title="User location map"
        src={mapEmbedUrl(location)}
        className="h-full w-full border-0"
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    </div>
  );
}
